import { useState } from "react";
import { Phone, MapPin, Instagram } from "lucide-react";
import { LogoMark } from "./Logo";
import LegalModals from "./LegalModals";
import { site, scrollToId } from "../config/site";

const nav = [
  { id: "inicio", label: "Inicio" },
  { id: "sobre-mi", label: "Sobre mí" },
  { id: "servicios", label: "Servicios" },
  { id: "faq", label: "Preguntas frecuentes" },
  { id: "contacto", label: "Contacto" },
];

export default function Footer() {
  const [legal, setLegal] = useState(null);

  return (
    <footer data-testid="site-footer" className="bg-atento-deep text-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="shrink-0 bg-ivory rounded-xl p-1.5 inline-flex">
                <LogoMark className="w-9 h-9" />
              </span>
              <div className="leading-none">
                <span className="block font-accent font-bold text-lg tracking-[0.14em]">
                  ATENTO
                </span>
                <span className="block text-[10px] font-accent font-medium uppercase tracking-[0.18em] text-sage mt-1">
                  Gabinete de Psicopedagogía
                </span>
              </div>
            </div>
            <p className="mt-5 font-serif italic text-xl text-ivory/85">«{site.slogan}»</p>
            <p className="mt-3 text-sm leading-relaxed text-ivory/60 max-w-sm">
              {site.fullName}. {site.role}: {site.owner}. Psicopedagogía en {site.city}.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-ivory/60 max-w-sm" data-testid="footer-areas-text">
              {site.areas}
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="font-accent font-semibold text-xs uppercase tracking-[0.18em] text-sage">
              Navegación
            </p>
            <ul className="mt-5 space-y-3">
              {nav.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => scrollToId(l.id)}
                    data-testid={`footer-link-${l.id}`}
                    className="text-sm text-ivory/70 hover:text-ivory transition-colors cursor-pointer"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="font-accent font-semibold text-xs uppercase tracking-[0.18em] text-sage">
              Contacto
            </p>
            <ul className="mt-5 space-y-3 text-sm text-ivory/70">
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-sage" strokeWidth={1.8} />
                <a href={site.phoneHref} data-testid="footer-phone-link" className="hover:text-ivory transition-colors">
                  {site.phoneDisplay}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-sage shrink-0" strokeWidth={1.8} />
                <span>
                  {site.address}, {site.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Instagram className="w-4 h-4 text-sage" strokeWidth={1.8} />
                <a
                  href={site.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram-link"
                  className="hover:text-ivory transition-colors"
                >
                  {site.instagramHandle}
                </a>
              </li>
            </ul>

            <p className="mt-8 font-accent font-semibold text-xs uppercase tracking-[0.18em] text-sage">
              Legal
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-ivory/70">
              <li>
                <button onClick={() => setLegal("aviso")} data-testid="footer-legal-aviso" className="hover:text-ivory transition-colors cursor-pointer">
                  Aviso legal
                </button>
              </li>
              <li>
                <button onClick={() => setLegal("privacidad")} data-testid="footer-legal-privacidad" className="hover:text-ivory transition-colors cursor-pointer">
                  Política de privacidad
                </button>
              </li>
              <li>
                <button onClick={() => setLegal("cookies")} data-testid="footer-legal-cookies" className="hover:text-ivory transition-colors cursor-pointer">
                  Política de cookies
                </button>
              </li>
              <li>
                <button
                  onClick={() => window.dispatchEvent(new Event("atento:cookies-open"))}
                  data-testid="footer-cookie-preferences"
                  className="hover:text-ivory transition-colors cursor-pointer"
                >
                  Preferencias de cookies
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-ivory/10 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-ivory/50">
            © {new Date().getFullYear()} {site.fullName} · {site.city}
          </p>
          <p className="text-xs text-ivory/40">
            Psicopedagogía · Evaluación psicopedagógica · Reeducación pedagógica · Orientación académica y familiar · Acompañamiento 0-6 años
          </p>
        </div>
      </div>

      <LegalModals open={legal} onClose={() => setLegal(null)} />
    </footer>
  );
}
